import React, { useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { X, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1/sentinel`;

function ExposureBadge({ exposure }) {
  const isBullish = exposure.impact === "bullish";
  const color = isBullish ? "#22c55e" : "#ef4444";
  const bgColor = isBullish ? "rgba(34, 197, 94, 0.1)" : "rgba(239, 68, 68, 0.1)";
  const borderColor = isBullish ? "rgba(34, 197, 94, 0.3)" : "rgba(239, 68, 68, 0.3)";
  const Icon = isBullish ? TrendingUp : TrendingDown;

  const tickerUrl = `https://finance.yahoo.com/quote/${exposure.ticker.replace("$", "")}`;

  return (
    <div
      data-testid={`exposure-${exposure.ticker}`}
      className="flex items-start gap-2.5 p-2.5 rounded-lg transition-all duration-150"
      style={{
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
      }}
    >
      <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
        <Icon size={12} style={{ color }} />
        <a
          href={tickerUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`exposure-ticker-link-${exposure.ticker}`}
          className="font-mono text-xs font-bold tracking-wide transition-opacity hover:opacity-80"
          style={{ color, textDecoration: "none" }}
        >
          {exposure.ticker}
        </a>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="font-mono text-[10px] font-medium" style={{ color: "#e6edf3" }}>
            {exposure.company}
          </span>
          <span
            className="font-mono text-[9px] px-1 py-0.5 rounded uppercase font-bold"
            style={{
              color,
              backgroundColor: isBullish ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
            }}
          >
            {exposure.impact}
          </span>
        </div>
        <p className="font-mono text-[11px] leading-relaxed" style={{ color: "#8b949e" }}>
          {exposure.exposure}
        </p>
      </div>
    </div>
  );
}

export default function DeepAnalysisPanel({ postId, onClose }) {
  const [content, setContent] = useState(null);
  const [exposures, setExposures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);

  // Fetch deep analysis on mount
  const fetchDeepAnalysis = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/deep-analysis`, { post_id: postId });
      setContent(res.data.content);
      setExposures(res.data.specific_exposures || []);
      setMeta({
        latency_ms: res.data.latency_ms,
        model: res.data.model,
        generated_at: res.data.generated_at,
      });
    } catch (err) {
      setError(err.response?.data?.detail || "Deep analysis failed");
    } finally {
      setLoading(false);
    }
  }, [postId]);

  React.useEffect(() => {
    fetchDeepAnalysis();
  }, [fetchDeepAnalysis]);

  return (
    <div
      data-testid="deep-analysis-panel"
      className="border-t"
      style={{ borderColor: '#30363d' }}
    >
      {/* Header bar */}
      <div
        className="flex items-center justify-between px-4 py-2 border-b"
        style={{
          backgroundColor: '#0d1117',
          borderColor: '#30363d',
        }}
      >
        <span
          className="font-mono text-[11px] tracking-[0.12em] uppercase"
          style={{ color: '#f59e0b' }}
        >
          Deep Analysis
        </span>
        <div className="flex items-center gap-3">
          {meta && (
            <span
              className="font-mono text-[10px]"
              style={{ color: '#484f58' }}
            >
              {meta.latency_ms}ms &middot; MacroFlare AI
            </span>
          )}
          <button
            data-testid="deep-analysis-close-button"
            onClick={onClose}
            className="p-0.5 rounded transition-colors duration-100"
            style={{ color: '#8b949e' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#e6edf3')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#8b949e')}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Content area */}
      <div
        className="px-4 py-3"
        style={{ backgroundColor: '#0d1117' }}
      >
        {loading && (
          <div
            data-testid="deep-analysis-loading"
            className="flex flex-col items-center justify-center py-6 gap-2"
          >
            <Loader2 size={20} className="cw-spin" style={{ color: '#f59e0b' }} />
            <span className="font-mono text-xs" style={{ color: '#8b949e' }}>
              Generating deep analysis...
            </span>
            <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>
              This takes 3-8 seconds
            </span>
          </div>
        )}

        {error && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-[6px] text-sm"
            style={{
              backgroundColor: '#2a1418',
              border: '1px solid #7f1d1d',
              color: '#fca5a5',
            }}
          >
            <span className="font-mono text-xs">{error}</span>
          </div>
        )}

        {content && (
          <>
            <div
              data-testid="deep-analysis-content"
              className="deep-analysis-markdown"
            >
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>

            {/* Specific Exposures Section */}
            {exposures.length > 0 && (
              <div data-testid="specific-exposures-section" className="mt-4 pt-3" style={{ borderTop: '1px solid #21262d' }}>
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="font-mono text-[11px] tracking-[0.12em] uppercase font-semibold"
                    style={{ color: '#f59e0b' }}
                  >
                    Specific Exposures
                  </span>
                  <span className="font-mono text-[9px]" style={{ color: '#484f58' }}>
                    {exposures.length} companies
                  </span>
                </div>
                <div className="space-y-2">
                  {exposures.map((exp, i) => (
                    <ExposureBadge key={`${exp.ticker}-${i}`} exposure={exp} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
